import type { HpPetType } from '@zg-rentals/ts-hotpads-api-client';
import { petSizeSortRank } from './petSize';

// Keyed by the generated HpPetType, lower-cased, so tsc fails here when hotpads-api adds or drops a type.
// Key order is display order; `all` is the listing-wide rule.
const PET_KIND_KEYS = {
  all: true,
  dog: true,
  cat: true,
  bird: true,
  fish: true,
  reptile: true,
  other: true,
} as const satisfies Record<Lowercase<`${HpPetType}`>, true>;

export type PetKind = keyof typeof PET_KIND_KEYS;

export const PET_KINDS = Object.keys(PET_KIND_KEYS) as Array<PetKind>;

const isPetKind = (value: string | null | undefined): value is PetKind =>
  value != null && Object.hasOwn(PET_KIND_KEYS, value);

// Drops exact duplicates only: two different policies for one type and size must both survive.
export function groupPetPoliciesByKind<P extends { petSize?: string | null }>(
  entries: Array<{ petType: string | undefined; policy: P }>,
): Partial<Record<PetKind, Array<P>>> {
  const unique = new Map<string, { kind: PetKind; policy: P }>();
  for (const { petType, policy } of entries) {
    const kind = petType?.toLowerCase();
    if (isPetKind(kind)) unique.set(`${kind}:${JSON.stringify(policy)}`, { kind, policy });
  }
  const byKind: Partial<Record<PetKind, Array<P>>> = {};
  for (const { kind, policy } of unique.values()) {
    (byKind[kind] ??= []).push(policy);
  }
  for (const policies of Object.values(byKind)) {
    policies?.sort((a, b) => petSizeSortRank(a.petSize) - petSizeSortRank(b.petSize));
  }
  return byKind;
}

// A dog or cat with no policy of its own follows the listing-wide `all` policy.
export function policiesForPet<P>(byKind: Partial<Record<PetKind, Array<P>>> | null | undefined, pet: 'dog' | 'cat'): Array<P> {
  const own = byKind?.[pet];
  return own?.length ? own : (byKind?.all ?? []);
}

// A pet type counts as allowed when any size policy allows it, denied only on an explicit false.
export function combinedAllowed(policies: Array<{ allowed?: boolean | null }>): boolean | null {
  if (policies.some((policy) => policy.allowed === true)) return true;
  if (policies.some((policy) => policy.allowed === false)) return false;
  return null;
}
